import { useCallback, useEffect, useState } from 'react'
import type { RefObject } from 'react'

import { useDebounceCallback, useResizeObserver } from '@buffer-mono/popcorn'

interface UseDrawerHeightOptions {
  drawerRef: RefObject<HTMLElement>
  isOpen: boolean
}

/**
 * Calculates and maintains the correct height for a mobile drawer,
 * accounting for banners and other elements above it.
 *
 * Handles:
 * - Initial height calculation when drawer opens
 * - Window resize and orientation changes
 * - DOM mutations (e.g., banner dismissal)
 * - Element resize
 *
 * @param options.drawerRef - Ref to the drawer element
 * @param options.isOpen - Whether the drawer is currently open
 * @returns The calculated height as a CSS string (e.g., "500px" or "calc(100dvh - 56px)")
 */
export function useDrawerHeight({
  drawerRef,
  isOpen,
}: UseDrawerHeightOptions): string {
  const [fullHeight, setFullHeight] = useState('calc(100dvh - 56px)')

  const calculateHeight = useCallback(() => {
    if (drawerRef.current) {
      const rect = drawerRef.current.getBoundingClientRect()
      const topOffset = rect.top
      setFullHeight(`calc(100dvh - ${topOffset}px)`)
    }
  }, [drawerRef])

  // Debounced for high-frequency events (MutationObserver)
  const debouncedCalculateHeight = useDebounceCallback(calculateHeight)

  // Fires immediately on leading edge for drawer open
  const calculateHeightOnOpen = useDebounceCallback(calculateHeight, 0, {
    leading: true,
    trailing: false,
  })

  // Recalculate when drawer element resizes
  useResizeObserver({
    ref: drawerRef,
    onResize: calculateHeight,
  })

  // Set up all listeners when drawer is open
  useEffect(() => {
    if (!isOpen) return

    // Calculate initial height
    calculateHeightOnOpen()

    // Window resize and orientation
    const handleResize = (): void => {
      calculateHeight()
    }
    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleResize)

    // Scope to the navbar header to detect banner changes without
    // observing the entire document tree.
    const observer = new MutationObserver(debouncedCalculateHeight)
    const observeTarget =
      document.getElementById('publish-navbar') ?? document.body
    observer.observe(observeTarget, {
      childList: true,
      subtree: true,
    })

    return (): void => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
      observer.disconnect()
      debouncedCalculateHeight.cancel()
    }
  }, [isOpen, calculateHeight, calculateHeightOnOpen, debouncedCalculateHeight])

  return fullHeight
}
